import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { getMe, ApiError, configureApi } from '@/lib/api.js'
import { ERR } from '@/lib/errors.js'
import { applyBranding, workspaceName } from '@/lib/branding.js'
import { useAuth } from '@/auth/AuthProvider.jsx'
import { ScreenMessage, Spinner } from '@/components/ui/feedback.jsx'
import { Button } from '@/components/ui/button.jsx'

/**
 * Workspace context: tenant, tier, branding, feature flags (§3.3, §3.4).
 *
 * One call — GET /api/me/features — boots the entire shell. It is also the
 * first authenticated request the app ever makes, which makes it the place
 * every infrastructure failure surfaces first, and therefore the place where
 * those failures have to be explained rather than swallowed.
 *
 * @typedef {object} TenantValue
 * @property {import('@/lib/api.js').Tenant} tenant
 * @property {import('@/lib/api.js').SessionUser} user
 * @property {Record<string, boolean>} features
 * @property {(key: string) => boolean} hasFeature
 * @property {string|null} role
 * @property {boolean} isOwner
 * @property {boolean} canAccessOperations
 * @property {string} workspaceName
 */

/** @type {React.Context<TenantValue>} */
const TenantContext = React.createContext(null)

export function TenantProvider ({ children }) {
  const { signOut } = useAuth()

  const { data, error, isPending, refetch, isRefetching } = useQuery({
    queryKey: ['me'],
    queryFn: getMe,
    staleTime: 5 * 60 * 1000,
    retry: (count, err) => {
      // Never retry a definitive answer. no_tenant_claim and
      // no_such_workspace are configuration facts — retrying them just delays
      // the explanation the user needs by three round trips.
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false
      return count < 2
    }
  })

  // Branding is applied as a side effect of the data arriving rather than
  // during render, because it mutates :root — a render-phase write would run
  // twice under StrictMode and fight itself.
  React.useEffect(() => {
    if (data?.tenant?.branding) applyBranding(data.tenant.branding)
  }, [data])

  // A 402 anywhere in the app means this provider's tenant payload is stale —
  // a webhook suspended the workspace while it was open. Re-reading it is what
  // moves the shell to the blocked screen (Phase 0b). Registered here rather
  // than in AuthProvider because this is the query that has to be invalidated.
  React.useEffect(() => {
    configureApi({ onSubscriptionBlocked: () => { refetch() } })
    return () => configureApi({ onSubscriptionBlocked: null })
  }, [refetch])

  if (isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas">
        <Spinner label="Loading your workspace" />
      </div>
    )
  }

  if (error) return <BootError error={error} onRetry={refetch} onSignOut={signOut} busy={isRefetching} />

  const value = {
    tenant: data.tenant,
    user: data.user,
    features: data.features ?? {},
    hasFeature: key => data.features?.[key] === true,
    // Phase 2 item 12: the caller's role in this workspace, FOR RENDERING
    // ONLY. It exists so an owner-only control can be hidden rather than shown
    // and then 403'd. Every decision that matters is made server-side on a
    // live read (middleware/requireRole.js) — a client that lied about this
    // would gain nothing.
    role: data.user?.role ?? null,
    isOwner: data.user?.role === 'owner',
    // Phase 9 D9.1 — whether to draw the Operations navigation group and let
    // /operations/* render. SAME POSTURE AS `role`: rendering only. D9.3 is
    // explicit that hiding the nav is courtesy and the server gate on every
    // /api/operations/* call is the boundary, so a client that forced this to
    // true would reach four screens whose every request 403s.
    canAccessOperations: data.user?.operations_access === true,
    workspaceName: workspaceName(data.tenant)
  }

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}

/**
 * The failure screens for the boot call.
 *
 * ── WHY EACH OF THESE IS SPELLED OUT ────────────────────────────────────────
 * Phase 0 deliberately made these failures loud at the API layer — a token
 * with no tenant answers 403 no_tenant_claim rather than returning an empty
 * result set, precisely so the problem could not hide. This component is where
 * that decision finally pays off: it is the first time a human sees it.
 *
 * The cardinal sin here would be rendering an empty dashboard. A contractor
 * seeing zero jobs cannot tell "your login is not linked to a workspace" from
 * "you have no jobs yet", and one of those is a support call about missing data
 * that will take an hour to diagnose.
 */
function BootError ({ error, onRetry, onSignOut, busy }) {
  const slug = error instanceof ApiError ? error.slug : null

  if (slug === ERR.NO_TENANT_CLAIM) {
    return (
      <ScreenMessage
        tone="warn"
        title="Your login is not linked to a workspace"
        detail={error.message}
        action={<Button variant="secondary" onClick={onSignOut} className="w-full">Sign out</Button>}
      >
        <p>
          You signed in successfully, but this account is not a member of any MBOS
          workspace yet — so there is nothing to show you.
        </p>
        <p>
          If your workspace was set up recently, ask whoever set it up to add you.
          This is not something you can fix from here.
        </p>
      </ScreenMessage>
    )
  }

  if (slug === ERR.NO_SUCH_WORKSPACE) {
    return (
      <ScreenMessage
        title="No workspace at this address"
        detail={error.message}
        action={<Button variant="secondary" onClick={onSignOut} className="w-full">Sign out</Button>}
      >
        <p>MBOS has no workspace configured for the address you are using.</p>
        <p>Check the web address, or contact whoever administers your account.</p>
      </ScreenMessage>
    )
  }

  if (slug === ERR.TENANT_MISMATCH) {
    return (
      <ScreenMessage
        title="This session belongs to another workspace"
        detail={error.message}
        action={<Button onClick={onSignOut} className="w-full">Sign in again</Button>}
      >
        <p>Your sign-in is for a different workspace than the one at this address.</p>
      </ScreenMessage>
    )
  }

  if (slug === ERR.NETWORK) {
    return (
      <ScreenMessage
        tone="warn"
        title="Could not reach MBOS"
        action={
          <Button onClick={onRetry} loading={busy} className="w-full">Try again</Button>
        }
      >
        <p>Check your internet connection. Nothing has been lost — this will pick up where you left off.</p>
      </ScreenMessage>
    )
  }

  return (
    <ScreenMessage
      title="Could not load your workspace"
      detail={error?.message}
      action={
        <div className="flex flex-col gap-2">
          <Button onClick={onRetry} loading={busy}>Try again</Button>
          <Button variant="ghost" onClick={onSignOut}>Sign out</Button>
        </div>
      }
    >
      <p>Something went wrong loading your account. Trying again usually resolves it.</p>
    </ScreenMessage>
  )
}

/** @returns {TenantValue} */
export function useTenant () {
  const ctx = React.useContext(TenantContext)
  if (!ctx) throw new Error('useTenant must be used inside <TenantProvider>')
  return ctx
}
